// src/App.jsx
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import Sidebar from "./components/common/Sidebar";
import NavigationHeader from "./components/common/NavigationHeader";
import OverviewPage from "./pages/OverviewPage";
import SessionPage from "./pages/SessionPage";
import SettingsPage from "./pages/SettingsPage";
import CalendarPage from "./pages/CalendarPage";
import PlayersListPage from "./pages/PlayersListPage";
import { useState, useEffect, lazy, Suspense } from "react";
import LoginPage from "./pages/LoginPage";
import PrivateRoute from "./components/PrivateRoute";
import { ThemeProvider } from '../../shared/common/ThemeContext';
import { SimulationProvider } from '../../shared/players/SimulationContext';
import SurveyPage from "./pages/SurveyPage";
import { SeasonProvider, clearStoredSeasons } from "../../shared/common/SeasonContext";
import MedicalImageryListPage from "../../shared/players/PlayersUpdateInfosListPage";
import PlayerMedicalImageryPage from "./pages/PlayerUpdateInfosPage";
import ReportsHub from "./pages/ReportsHub";
import ClubReports from "../../shared/rapport/reportspages/club/ClubReports";
import PlayersPositionReport from "../../shared/rapport/reportspages/playerposition/PlayerPositionReport";
import CollectiveReport from "../../shared/rapport/reportspages/collective/CollectiveReport";
import WeeklyRecapReport from "../../shared/rapport/reportspages/weeklyrecap/WeeklyRecapReport";
import { useLocation } from "react-router-dom";
import { fetchAuth } from "../../shared/common/api";
import { invalidateMeCache } from "../../shared/common/useMe";
import { invalidateJsonCache } from "../../shared/common/fetchJsonCache";
import { useTheme } from "../../shared/common/ThemeContext";
import { PlayersPerfProvider } from "../../shared/common/PlayersPerfContext";
import { GlobalStatsProvider } from "../../shared/common/GlobalStatsContext";
import MultiSeasonReport from "../../shared/rapport/reportspages/season/MultiSeasonReport";
import BlockedPage from "../../shared/common/BlockedPage";
import RateLimitToast from "../../shared/common/RateLimitToast";
import RateLimitOverlay from "../../shared/common/RateLimitOverlay";
import V2App from "../../shared/v2/layout/V2App";

export const IS_DEV = import.meta.env.MODE === "development";
const PlayerDetails = lazy(() => import("./pages/PlayerDetailsPage"));

function PlayerDetailsFallback() {
  return (
    <div className="mx-auto px-4 py-6 lg:px-8">
      <div className="animate-pulse space-y-4">
        <div className="h-16 rounded-2xl bg-gray-300/40 dark:bg-gray-700/40" />
        <div className="h-[420px] rounded-2xl bg-gray-300/40 dark:bg-gray-700/40" />
      </div>
    </div>
  );
}

function ThemedShell({ children }) {
  const { isDarkMode } = useTheme();
  return (
    <div
      className={`flex h-screen w-full overflow-hidden ${
        isDarkMode ? "bg-gray-950 text-gray-100" : "bg-white text-gray-900"
      }`}
    >
      {children}
    </div>
  );
}

function App() {
  const [players, setPlayers] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [clubId, setClubId] = useState(() => localStorage.getItem("club_id"));

  const navigate = useNavigate();
  const location = useLocation();
  const isV2Route = location.pathname.startsWith("/v2");

  useEffect(() => {
    const user_id = localStorage.getItem("user_id");
    const storedToken = localStorage.getItem("token");
    const storedClubId = localStorage.getItem("club_id");

    setIsAuthenticated(!!user_id);
    setToken(storedToken);
    setClubId(storedClubId);
  }, []);

  useEffect(() => {
    if (isV2Route) {
      return;
    }

    if (!localStorage.getItem("token")) {
      return;
    }

    const trackPageView = () => {
      fetchAuth(`/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "pageview",
          event_name: "page_view",
          club_id: localStorage.getItem("user_id"),
          url: window.location.href,
          timestamp: new Date().toISOString(),
        }),
      })
    };

    trackPageView();
  }, [isV2Route, location]);

  const handleLogout = () => {
    localStorage.removeItem("user_id");
    localStorage.removeItem("club_id");
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    // Club-specific cached data
    clearStoredSeasons(clubId);
    localStorage.removeItem("simulations");
    localStorage.removeItem("simulationsQueue");

    invalidateMeCache({ refetch: false });
    invalidateJsonCache();

    setIsAuthenticated(false);
    setToken(null);
    setClubId(null);

    navigate("/");
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("simulations");
      localStorage.removeItem("simulationsQueue");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <ThemeProvider>
      <SeasonProvider key={clubId || "no-club"} clubId={clubId}>
          <SimulationProvider>
          <PlayersPerfProvider token={isV2Route ? null : token}>
            <GlobalStatsProvider token={isV2Route ? null : token}>
              <ThemedShell>
                <RateLimitToast />
                <RateLimitOverlay />
                {isAuthenticated && !isV2Route && <Sidebar handleLogout={handleLogout} />}
                <main
                  className={`relative flex-1 h-screen ${
                    isV2Route
                      ? "overflow-hidden"
                      : "overflow-y-auto overflow-x-hidden"
                  }`}
                >
                  {isAuthenticated && !isV2Route && <NavigationHeader />}
                  <Suspense fallback={<PlayerDetailsFallback />}>
                  <Routes>
                  <Route
                    path="/v2/*"
                    element={
                      <V2App
                        setToken={setToken}
                        setClubId={setClubId}
                        setIsAuthenticated={setIsAuthenticated}
                      />
                    }
                  />
                  <Route
                    path="/"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/overview" />
                      ) : (
                        <LoginPage
                          setIsAuthenticated={setIsAuthenticated}
                          setToken={setToken}
                          setClubId={setClubId}
                        />
                      )
                    }
                  />
                  <Route
                    path="/blocked"
                    element={<BlockedPage onLogout={handleLogout} />}
                  />
                  <Route
                    path="/overview"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={OverviewPage}
                      />
                    }
                  />
                  <Route
                    path="/session"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SessionPage}
                      />
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SettingsPage}
                      />
                    }
                  />
                  <Route
                    path="/calendar"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={CalendarPage}
                      />
                    }
                  />
                  <Route
                    path="/playersperfs"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        blockRestrictedClubUsers
                        element={PlayersListPage}
                        setPlayers={setPlayers}
                        players={players}
                      />
                    }
                  />
                  <Route
                    path="/players/:id"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayerDetails}
                        players={players}
                        motionVisibility={IS_DEV}
                      />
                    }
                  />
                  <Route
                    path="/survey"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={SurveyPage}
                      />
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={ReportsHub}
                      />
                    }
                  />
                  <Route
                    path="/reports/club"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={ClubReports}
                      />
                    }
                  />
                  <Route
                    path="/reports/players"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayersPositionReport}
                      />
                    }
                  />
                  <Route
                    path="/reports/weekly-recap"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={WeeklyRecapReport}
                        isDev={IS_DEV}
                      />
                    }
                  />
                  <Route
                    path="/reports/collectif"
                    element={
                      IS_DEV ? (
                        <PrivateRoute
                          isAuthenticated={isAuthenticated}
                          element={CollectiveReport}
                        />
                      ) : (
                        <Navigate to="/reports" replace />
                      )
                    }
                  />
                  <Route
                    path="/reports/multi-season"
                    element={
                      IS_DEV ? (
                        <PrivateRoute
                          isAuthenticated={isAuthenticated}
                          element={MultiSeasonReport}
                        />
                      ) : (
                        <Navigate to="/reports" replace />
                      )
                    }
                  />
                  <Route
                    path="/update-players-infos"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={MedicalImageryListPage}
                        players={players}
                        setPlayers={setPlayers}
                        onCardClick={(id) =>
                          navigate(`/update-players-infos/${id}`)
                        }
                      />
                    }
                  />
                  <Route
                    path="/update-players-infos/:playerId"
                    element={
                      <PrivateRoute
                        isAuthenticated={isAuthenticated}
                        element={PlayerMedicalImageryPage}
                      />
                    }
                  />
                  <Route
                    path="*"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/overview" />
                      ) : (
                        <LoginPage
                          setIsAuthenticated={setIsAuthenticated}
                          setToken={setToken}
                          setClubId={setClubId}
                        />
                      )
                    }
                  />
                  </Routes>
                  </Suspense>
                </main>
              </ThemedShell>
            </GlobalStatsProvider>
          </PlayersPerfProvider>
          </SimulationProvider>
        </SeasonProvider>
    </ThemeProvider>
  );
}

export default App;
